import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import eyvoLogo from '../../../assets/eyvo_logo.png';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, X, Eye, EyeOff, RefreshCw, Settings, Users, Edit, Shield, AlertTriangle } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = '844565024014-ap42nanrspqvirdfndk1f68tcadl3mai.apps.googleusercontent.com';
function apiWithToken(token) { const i = axios.create({ baseURL: BACKEND_URL }); i.interceptors.request.use(c => { c.headers.Authorization = 'Bearer ' + token; return c; }); return i; }

export default function EyvoAdminLogin({ onLogin }) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/eyvo-admin/login/google`, {
        credential: credentialResponse.credential,
      });
      onLogin(res.data.access_token, res.data.staff);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed. Ensure your Google account is registered as Eyvo staff.');
    }
    setLoading(false);
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--sidebar))' }}>
        <div className="w-full max-w-md p-8 bg-card rounded-2xl shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <img src={eyvoLogo} alt="Eyvo" className="h-14 w-auto mb-5"
              style={{ filter: 'hue-rotate(0deg) saturate(1)' }} />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Eyvo Admin Portal</h1>
            <p className="text-sm text-muted-foreground mt-1 text-center">{t('term.restrictedMsg')}</p>
          </div>

          {/* Security notice */}
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 mb-6">
            <Shield className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              This portal uses <strong>Google Authentication only</strong>. Sign in with your <strong>Eyvo Google Workspace account</strong>.
              {t('term.unauthorisedMsg')}
            </p>
          </div>

          {/* Google Sign-In — only authentication method */}
              <div className="flex justify-center">
                {loading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="h-4 w-4 animate-spin" /> Verifying credentials…
                  </div>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google sign-in failed. Please try again.')}
                    theme="outline"
                    size="large"
                    text="signin_with"
                    shape="rectangular"
                    logo_alignment="left"
                    width="320"
                  />
                )}
              </div>

          {error && (
            <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            Not an Eyvo staff member? Contact <a href="mailto:mike@eyvo.com" className="text-primary hover:underline">mike@eyvo.com</a>
          </p>
          <a href="/login" className="block text-center text-xs text-primary hover:underline mt-3">
            &larr; Back to main system login
          </a>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}
